import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import _, { isFunction } from 'lodash';
import { BS_TOOLBAR_STATUS } from '../../../comm/constant';
import { bedStatusAPI } from '../../../apis';
import Toolbar from '../../BedStatus/newComponents/Toolbar';
import { useActionableTicketReminder } from '../../../utils/hooks';

const FloorPlan = lazy(() => import('../../BedStatus/newComponents/Landing/FloorPlan'));

const useStyles = makeStyles(() => ({
  root: ({ toolStatus, fullScreen, remarkBar, sso }) => {
    let style = {
      scale: toolStatus.zoom,
      transformOrigin: 'left top',
      cursor: 'auto',
      userSelect: 'auto',
    };

    if (toolStatus.type === BS_TOOLBAR_STATUS.MOUSE) {
      style.cursor = 'auto';
      style.userSelect = 'auto';
    } else if (toolStatus.type === BS_TOOLBAR_STATUS.MOVE) {
      style.cursor = 'grab';
      style.userSelect = 'none';
    }

    if (fullScreen || sso) {
      if (remarkBar) {
        style.maxHeight = `calc(100vh - 4.8rem - 15.2rem)`;
      } else {
        style.maxHeight = `calc(100vh - 3.75rem)`;
      }
    } else if (remarkBar) {
      style.maxHeight = `calc(100vh - 10.125rem - 15.2rem)`;
    } else {
      style.maxHeight = `calc(100vh - 10.125rem)`;
    }

    return style;
  },
}));

const Landing = () => {
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const sso = useSelector((state) => state.ssoReducer);
  const [isDragging, setIsDragging] = useState(false);
  const [toolStatus, setToolStatus] = useState({ type: BS_TOOLBAR_STATUS.MOVE, zoom: 1, reset: 0 });
  const [remarkBar, setRemarkBar] = useState(false);
  const classes = useStyles({ toolStatus, isDragging, fullScreen, remarkBar, sso });
  const WardBoxRef = useRef(null);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });
  const [floorPlan, setFloorPlan] = useState([]);
  const [popup, setPopup] = useState(false);
  const rouList = useSelector((state) => state.rouList);
  useActionableTicketReminder('audio', 'AD');

  useEffect(() => {
    WardBoxRef.current.scroll(0, 0);
  }, [toolStatus.reset]);

  const handleMouseDown = useCallback(
    (event) => {
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || popup) {
        return;
      }
      setIsDragging(true);
      setStartPosition({
        x: event.clientX,
        y: event.clientY,
        scrollLeft: WardBoxRef.current.scrollLeft,
        scrollTop: WardBoxRef.current.scrollTop,
      });
    },
    [toolStatus, WardBoxRef, popup]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE || popup) {
        return;
      }
      if (isDragging) {
        const deltaX = event.clientX - startPosition.x;
        const deltaY = event.clientY - startPosition.y;
        const scrollLeft = startPosition.scrollLeft - deltaX;
        const scrollTop = startPosition.scrollTop - deltaY;
        WardBoxRef.current.scroll(scrollLeft, scrollTop);
      }
    },
    [toolStatus, startPosition, isDragging, popup]
  );

  const handleMouseUp = useCallback(() => {
    if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE) {
      return;
    }
    setIsDragging(false);
  }, [toolStatus]);

  const handleMouseLeave = useCallback(() => {
    if (toolStatus.type !== BS_TOOLBAR_STATUS.MOVE) {
      return;
    }
    setIsDragging(false);
  }, [toolStatus]);

  const getFloorPlanListAPI = useCallback(async (callback) => {
    try {
      const { result } = await bedStatusAPI.getFloorPlanList();
      setFloorPlan(result?.floorplans);
      if (isFunction(callback)) {
        callback();
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    getFloorPlanListAPI();

    const refresh = setInterval(() => {
      const dialogs = document.getElementsByClassName('MuiDialog-container');
      if (dialogs.length === 0) {
        getFloorPlanListAPI();
      }
    }, 3 * 60 * 1000);

    return () => {
      clearInterval(refresh);
    };
  }, [getFloorPlanListAPI, rouList]);

  useEffect(() => {
    setRemarkBar(false);
  }, [fullScreen]);

  const floorMemo = useMemo(() => {
    return _.orderBy(floorPlan, 'wardLocation');
  }, [floorPlan]);

  return (
    <Box
      display="flex"
      padding="1.25rem 1.3rem 0"
      gridGap="1.3rem"
      ref={WardBoxRef}
      overflow="hidden"
      minHeight={`calc(100vh - ${sso ? 0 : 4.375}rem - 4.2rem)`}
      maxHeight={`calc(100vh - ${sso ? 0 : 4.375}rem - 4.2rem)`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      <Toolbar setStatus={setToolStatus} status={toolStatus} />
      <Box className={classes.root} display="flex" flexDirection="column" gridGap="2rem">
        {floorMemo.map((item) => (
          <Suspense fallback={<div />} key={item.hospCode + item.wardLocation}>
            <FloorPlan allData={item} getFloorPlanListAPI={getFloorPlanListAPI} setPopup={setPopup} admission={true} />
          </Suspense>
        ))}
      </Box>
    </Box>
  );
};

export default Landing;
